import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '@/components/Sidebar';
import Header from '@/components/Header';
import BottomNavigation from '@/components/BottomNavigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useAuthLite } from '@/context/AuthLite';
import { LogOut } from 'lucide-react';
import { OfflineConnectivityBanner } from '@/components/OfflineConnectivityBanner';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { subscribeCrmSyncPendingCount } from '@/lib/crm-sync-outbox';
import { subscribeImageUploadOutboxPendingCount } from '@/lib/image-upload-outbox';
import { getOfflineSyncRequireReview } from '@/lib/offline-sync-settings';

const SIDEBAR_WIDTH_REM = 5; // w-20 = 5rem, matches Sidebar desktop width

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loginTransitionActive, setLoginTransitionActive] = useState(false);
  const [overlayShrunk, setOverlayShrunk] = useState(false);
  const [contentVisible, setContentVisible] = useState(true);
  const overlayRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { user, notApproved, logout } = useAuthLite();
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const [pendingTotal, setPendingTotal] = useState(0);
  useEffect(() => {
    let img = 0, crm = 0;
    const up = () => setPendingTotal(img + crm);
    const u1 = subscribeImageUploadOutboxPendingCount((n) => { img = n; up(); });
    const u2 = subscribeCrmSyncPendingCount((n) => { crm = n; up(); });
    return () => { u1(); u2(); };
  }, []);
  const bannerVisible = !online || (online && pendingTotal > 0 && getOfflineSyncRequireReview());

  useEffect(() => {
    const flag = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('loginTransition');
    if (isMobile && flag) {
      sessionStorage.removeItem('loginTransition');
      return;
    }
    if (flag && !isMobile) {
      setLoginTransitionActive(true);
      setContentVisible(false);
      const t = requestAnimationFrame(() => {
        requestAnimationFrame(() => setOverlayShrunk(true));
      });
      return () => cancelAnimationFrame(t);
    }
  }, [isMobile]);

  const handleOverlayTransitionEnd = (e: React.TransitionEvent) => {
    if (e.propertyName !== 'width' || !overlayRef.current) return;
    sessionStorage.removeItem('loginTransition');
    setLoginTransitionActive(false);
    setOverlayShrunk(false);
    setContentVisible(true);
  };

  if (user && notApproved) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground p-6">
        <div className="max-w-md w-full rounded-xl border border-border bg-card p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-foreground mb-2">{t('auth.accountPendingApproval')}</h1>
          <p className="text-muted-foreground mb-6">
            {t('auth.accountPendingDescription')}
          </p>
          <button
            type="button"
            onClick={() => logout()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90"
          >
            <LogOut className="h-4 w-4" />
            {t('header.logout')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-background text-foreground overflow-hidden safe-area-x">
      {/* Post-login overlay: blue strip shrinks from 40% to sidebar width, then content fades in */}
      {loginTransitionActive && (
        <div
          ref={overlayRef}
          role="presentation"
          className="fixed left-0 top-0 bottom-0 z-[100] bg-sidebar"
          style={{
            width: overlayShrunk ? `${SIDEBAR_WIDTH_REM}rem` : '40vw',
            transition: 'width 0.5s ease-out',
          }}
          onTransitionEnd={handleOverlayTransitionEnd}
        />
      )}

      {/* Sidebar */}
      <Sidebar open={sidebarOpen} onOpenChange={setSidebarOpen} />

      {/* Main Content */}
      <div
        className={cn(
          "flex-1 flex flex-col min-w-0 md:ml-20 overflow-hidden transition-opacity duration-300",
          isMobile && "overflow-x-hidden max-w-full !ml-0",
          !contentVisible && "opacity-0"
        )}
      >
        {/* Header */}
        <div className={cn(
          "relative",
          isMobile && "fixed top-0 left-0 right-0 z-50"
        )}>
          <Header onMenuClick={() => setSidebarOpen(true)} />
          <OfflineConnectivityBanner />
        </div>

        {/* Page Content */}
        <main className={cn(
          "flex-1 min-h-0 min-w-0 overflow-y-auto",
          isMobile &&
            cn(
              "pb-12 overflow-x-hidden max-w-full",
              !bannerVisible
                ? "pt-[calc(4rem+env(safe-area-inset-top))]"
                : "pt-[calc(4rem+2.75rem+env(safe-area-inset-top))]",
            ),
        )}>
          {children}
        </main>
      </div>

      {/* Bottom Navigation (Mobile only) */}
      <BottomNavigation />
    </div>
  );
}
